/**
 * Durability contract (FN-5): is this project's CURRENT content represented in
 * the cloud, for THIS account?
 *
 * This exists because `store.isDirty()` cannot answer that question and was
 * being asked it. `isDirty` is the LOCAL persistence flag — a successful
 * IndexedDB autosave clears it with zero cloud calls — so "not dirty" was
 * authorising sign-out to delete the only copy of work that had never left the
 * device. Offline, that is the whole session.
 *
 * The answer is a three-value verdict, never a boolean, and the third value is
 * the point: **unknown counts as unsynced**. A caller may only delete a local
 * copy on `synced`, which requires positive evidence on four axes at once —
 * the account, the project, the content stamp, and an unchanged revision.
 *
 * Pure by construction: every input is passed in, nothing is imported, and no
 * clock or global is read. `sync` is the provenance record
 * `save-load.js` stamps in `markCloudSynced()` when a save is CONFIRMED.
 */

export const DURABILITY = Object.freeze({
  SYNCED: 'synced',
  UNSYNCED: 'unsynced',
  UNKNOWN: 'unknown'
});

const verdict = (status, reason) => Object.freeze({ status, reason });

/**
 * @param {Object} opts
 * @param {Object|null} opts.state          — the live project state
 * @param {string|null} [opts.principal]    — the account scope this tab belongs to
 * @param {{updatedAt: number, principal: string|null}|null} [opts.sync]
 *   Provenance of the last CONFIRMED cloud save for this project, or null when
 *   this session has never confirmed one (a fresh boot recovering from autosave
 *   has none — which is exactly a case that must not be read as durable).
 * @param {number|null} [opts.revisionAtSaveStart]
 * @param {number|null} [opts.revisionNow]
 *   The store revision either side of an upload. A move between them means the
 *   user edited while the save was in flight, so what landed is already stale.
 * @returns {{status: string, reason: string}}
 */
export function evaluateDurability({
  state = null,
  principal = null,
  sync = null,
  revisionAtSaveStart = null,
  revisionNow = null
} = {}) {
  if (!state || typeof state !== 'object') return verdict(DURABILITY.UNKNOWN, 'no-state');
  if (!state.projectId) return verdict(DURABILITY.UNKNOWN, 'no-project-id');

  // `cloudVersion` is the persisted latch: an integer means the server has
  // acknowledged this project at least once. Its absence is not ambiguous —
  // the project has never been in the cloud at all.
  if (!Number.isInteger(state.cloudVersion)) return verdict(DURABILITY.UNSYNCED, 'never-in-cloud');

  const syncedAt = Number(sync?.updatedAt);
  if (!Number.isFinite(syncedAt) || syncedAt <= 0) {
    return verdict(DURABILITY.UNKNOWN, 'no-sync-provenance');
  }

  // POSITIVE account provenance, both sides. The first version of this only
  // refused when both principals were truthy, so a confirmation recorded with
  // no principal — or evaluated with none — read as proof. Missing evidence is
  // not agreement; it is missing evidence.
  if (!principal) return verdict(DURABILITY.UNKNOWN, 'no-principal');
  if (!sync?.principal) return verdict(DURABILITY.UNKNOWN, 'no-sync-principal');
  if (sync.principal !== principal) return verdict(DURABILITY.UNKNOWN, 'synced-by-other-account');

  const live = Number(state.updatedAt);
  if (!Number.isFinite(live) || live <= 0) return verdict(DURABILITY.UNKNOWN, 'no-content-stamp');
  // EQUALITY, not "not newer". A local stamp OLDER than the acknowledged one is
  // still content the server has not seen — an undo is the ordinary way to
  // produce it — and "older therefore safe to delete" would discard exactly
  // that. The only stamp that proves this content is on the server is the one
  // the server acknowledged.
  if (live !== syncedAt) {
    return verdict(
      DURABILITY.UNSYNCED,
      live > syncedAt ? 'edits-after-sync' : 'content-rolled-back'
    );
  }

  // A revision comparison is REQUIRED evidence wherever the caller can supply
  // it. Both absent means the caller is asking about a moment rather than a
  // span (the first read, before any save) and the stamp equality above stands
  // alone; one absent means the caller tried to measure a span and could not.
  const spanStart = revisionAtSaveStart === null ? null : Number(revisionAtSaveStart);
  const spanEnd = revisionNow === null ? null : Number(revisionNow);
  if ((spanStart === null) !== (spanEnd === null)) {
    return verdict(DURABILITY.UNKNOWN, 'incomplete-revision-evidence');
  }
  if (spanStart !== null && spanStart !== spanEnd) {
    return verdict(DURABILITY.UNSYNCED, 'edits-during-save');
  }

  return verdict(DURABILITY.SYNCED, 'confirmed');
}

/**
 * The ONE predicate any caller may use before deleting a local copy. Written as
 * a function rather than an inline `=== 'synced'` so that the refusal is a
 * property of this module and cannot be loosened at a call site.
 */
export function isSafeToDiscardLocalCopy(result) {
  return result?.status === DURABILITY.SYNCED;
}
